import { useCallback } from "react";

import { useCsv } from "@/hooks/use-csv";
import { CsvFileNamePrefixes, CsvHeaders } from "@/hooks/use-csv/constants";
import { escapeCsvQuestionHeader, quoteCsvField } from "@/hooks/use-csv/csv-field";

import { useHandleReservation } from "@/components/features/management/reservation/search/_shared";

import type * as _Model from "../../../../../model";

type Props = {
	viewData: _Model.Type.ViewData;
};

export const useHandleCsvDownload = ({
	viewData: {
		csvDownloadData: { hotelName, hotelPrefecture, reservationList },
	},
}: Props) => {
	const { csvGenerate, csvDownload } = useCsv();
	const {
		// 宿泊券の合計金額を算出する
		onCalcTotalVoucherAmount,
		// ディスカウントの合計金額を算出する
		onCalcTotalDiscountAmount,
		// ディスカウント前のゲストが支払う金額を算出する
		onCalcPrevTotalGuestChargeAmount,
		// 通算での宿泊数を算出する
		onCalcTotalStayCount,
		// 宿泊券名をリストで返却する
		onGetVoucherNameList,
		// チェックアウト日を返却する
		onGetCheckOutDate,
		// 支払方法テキストを返却する
		onGetPaymentMethodText,
		// NO SHOW テキストを返却する
		onGetNoShowText,
		// プラン名をリストで返却する
		onGetPlanNameList,
		// オプション名をリストで返却する
		onGetOptionNameList,
		// 部屋名をリストで返却する
		onGetRoomNameList,
		// ディスカウント名とディスカウント金額を返却する
		onGetDiscountNameAndDiscountPrice,
		// チェックイン時刻を返却する
		onGetCheckInTimeText,
		// 電話番号を返却する
		onGetGuestTelText,
	} = useHandleReservation();

	const onCsvDownload = useCallback(() => {
		const csvHeaders = [...CsvHeaders.reservation.perReservation];
		// 質問のヘッダーを追加
		for (const reservation of reservationList) {
			for (const question of reservation.questions) {
				const escapedQuestion = escapeCsvQuestionHeader(question.question);
				/**
				 * 重複していなければ
				 */
				if (!csvHeaders.includes(escapedQuestion)) csvHeaders.push(escapedQuestion);
			}
		}

		/**
		 * ホテル名とホテル所在地は質問の後に表示するため
		 * 一旦 `csvHeaders` からホテル名とホテル所在地を削除し、
		 * 質問の後にホテル名とホテル所在地を追加する
		 */
		csvHeaders.splice(csvHeaders.indexOf("ホテル名"), 1);
		csvHeaders.splice(csvHeaders.indexOf("ホテル所在地"), 1);
		csvHeaders.push("ホテル名", "ホテル所在地");

		const rows = reservationList.map((reservation) => {
			const csvRows: Array<string | number> = Array.from({ length: csvHeaders.length }, () => "");

			csvRows[csvHeaders.indexOf("予約サイト名")] = "CHILLNN";
			csvRows[csvHeaders.indexOf("宿泊日")] = reservation.checkIn;
			csvRows[csvHeaders.indexOf("予約ID")] = reservation.reservationID;
			csvRows[csvHeaders.indexOf("予約成立日")] = reservation.reserveDate;
			csvRows[csvHeaders.indexOf("最終予約更新日")] = reservation.latestUpdateDate;
			csvRows[csvHeaders.indexOf("チェックイン日")] = reservation.checkIn;
			const { checkOutDate } = onGetCheckOutDate({
				checkIn: reservation.checkIn,
				reservationType: reservation.reservationType,
				checkOut: reservation.checkOut,
			});
			csvRows[csvHeaders.indexOf("チェックアウト日")] = checkOutDate;
			const { totalStayCount } = onCalcTotalStayCount({
				checkIn: reservation.checkIn,
				checkOut: checkOutDate,
			});
			csvRows[csvHeaders.indexOf("泊数")] = totalStayCount;
			csvRows[csvHeaders.indexOf("宿泊者氏名")] = reservation.guestName;
			csvRows[csvHeaders.indexOf("宿泊者氏名（カナ）")] = reservation.guestNameKana ?? "";
			csvRows[csvHeaders.indexOf("部屋マスタ")] = reservation.room
				.map(({ name, date }) => `【${date}】 ${name} `)
				.join(`\n`);
			const { roomNameList: canceledRoomNameList } = onGetRoomNameList({ roomList: reservation.roomCanceled });
			csvRows[csvHeaders.indexOf("キャンセル済み部屋マスタ")] = canceledRoomNameList.join(`\n`);
			csvRows[csvHeaders.indexOf("プラン名称")] = reservation.plan ?? "";
			const { planNameList: canceledPlanNameList } = onGetPlanNameList({ planList: reservation.planCanceled });
			csvRows[csvHeaders.indexOf("キャンセル済みプラン名称")] = canceledPlanNameList.join(`\n`);
			csvRows[csvHeaders.indexOf("部屋数")] = reservation.numOfRoom;
			csvRows[csvHeaders.indexOf("大人人数")] = reservation.numOfAdult;
			csvRows[csvHeaders.indexOf("子供人数")] = reservation.numOfKids;
			const { voucherNameList } = onGetVoucherNameList({ voucherList: reservation.vouchers });
			csvRows[csvHeaders.indexOf("ホテルチケット")] = voucherNameList.join("\n");
			const { totalVoucherAmount } = onCalcTotalVoucherAmount({ voucherList: reservation.vouchers });
			const { totalDiscountAmount } = onCalcTotalDiscountAmount({
				discountAmount: reservation.priceDiscount,
				totalVoucherAmount: totalVoucherAmount,
			});
			const { prevTotalGuestChargeAmount } = onCalcPrevTotalGuestChargeAmount({
				totalGuestChargeAmount: reservation.totalGuestCharge,
				totalDiscountAmount: totalDiscountAmount,
			});
			csvRows[csvHeaders.indexOf("ディスカウント前金額")] = prevTotalGuestChargeAmount;
			const { discountNameAndDiscountPriceText } = onGetDiscountNameAndDiscountPrice({
				discount: reservation.discount || undefined,
				discountPrice: reservation.priceDiscount || undefined,
			});
			csvRows[csvHeaders.indexOf("ディスカウント")] = discountNameAndDiscountPriceText;
			csvRows[csvHeaders.indexOf("プラン売上金額")] = reservation.pricePlanTotal;
			csvRows[csvHeaders.indexOf("お部屋売上金額")] = reservation.priceRoomTotal;
			csvRows[csvHeaders.indexOf("オプション売上金額")] = reservation.priceOptionTotal;
			csvRows[csvHeaders.indexOf("全ての宿泊予約に対して課する料金合計")] = reservation.priceAdditionalFeeTotal;
			csvRows[csvHeaders.indexOf("請求料金")] = reservation.totalGuestCharge;
			const { paymentMethodText } = onGetPaymentMethodText({ paymentMethod: reservation.paymentMethod });
			csvRows[csvHeaders.indexOf("支払方法")] = paymentMethodText;
			const { optionNameList } = onGetOptionNameList({ optionList: reservation.option });
			csvRows[csvHeaders.indexOf("オプション")] = optionNameList.join(`\n`);
			const { optionNameList: canceledOptionNameList } = onGetOptionNameList({
				optionList: reservation.optionCanceled,
			});
			csvRows[csvHeaders.indexOf("キャンセル済みオプション")] = canceledOptionNameList.join(`\n`);

			const { checkInTimeText } = onGetCheckInTimeText({
				checkInTimeStart: reservation.checkInTimeStart,
				checkInTimeEnd: reservation.checkInTimeEnd,
			});
			csvRows[csvHeaders.indexOf("チェックイン時刻")] = checkInTimeText;

			csvRows[csvHeaders.indexOf("メールアドレス")] = reservation.guestEmail;
			csvRows[csvHeaders.indexOf("郵便番号")] = reservation.guestPostalCode;
			csvRows[csvHeaders.indexOf("住所")] = reservation.guestAddress;

			const { guestTelText } = onGetGuestTelText({ guestTel: reservation.guestTel });
			csvRows[csvHeaders.indexOf("電話番号")] = guestTelText;

			csvRows[csvHeaders.indexOf("キャンセル日")] = reservation.canceledDate ?? "";
			csvRows[csvHeaders.indexOf("備考")] = ""; // 常にブランク
			const { noShowText } = onGetNoShowText({ noShow: reservation.isNoShow });
			csvRows[csvHeaders.indexOf("NO SHOW")] = noShowText;
			csvRows[csvHeaders.indexOf("スタッフメモ")] = reservation.memoByHost ?? "";
			for (const question of reservation.questions) {
				const escapedQuestion = escapeCsvQuestionHeader(question.question);
				const header = csvHeaders.indexOf(escapedQuestion);
				if (header !== -1) {
					csvRows[header] = question.answer.map((answer) => answer).join(", ");
				}
			}

			csvRows[csvHeaders.indexOf("ホテル名")] = hotelName;
			csvRows[csvHeaders.indexOf("ホテル所在地")] = hotelPrefecture;

			return csvRows.map(quoteCsvField).join(",");
		});

		const csv = csvGenerate(csvHeaders, rows);
		csvDownload(CsvFileNamePrefixes.reservation, csv);
	}, [
		csvDownload,
		csvGenerate,
		hotelName,
		hotelPrefecture,
		onCalcPrevTotalGuestChargeAmount,
		onCalcTotalDiscountAmount,
		onCalcTotalStayCount,
		onCalcTotalVoucherAmount,
		onGetCheckInTimeText,
		onGetCheckOutDate,
		onGetDiscountNameAndDiscountPrice,
		onGetGuestTelText,
		onGetNoShowText,
		onGetOptionNameList,
		onGetPaymentMethodText,
		onGetPlanNameList,
		onGetRoomNameList,
		onGetVoucherNameList,
		reservationList,
	]);

	return {
		onCsvDownload,
	};
};
